import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Button, Alert } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import PostgRESTTable from "../shared/PostgRESTTable";
import StoryIdeaStatusBadge from "./StoryIdeaStatusBadge";
import StoryIdeaActions from "./StoryIdeaActions";
import PublishCountdown from "./PublishCountdown";
import RelativeTime from "./RelativeTime";

// constants
const PAGE_SIZE = 7;
const HIGHLIGHT_DURATION_MS = 2500;

// Tabs surface the distinct workflows for each lifecycle state. The
// table itself is the same shape on each tab — only the static filter
// and default sort change. URL state (?tab=…) makes the chosen tab
// shareable / refresh-safe, matching RequestsAdminPanel's pattern.
//
// - Draft     → active workspace; admins iterate, sort by most-recently-edited.
// - Scheduled → quick check; sort by next-to-publish.
// - Published → quick check; sort by most-recently-published.
// - All       → fallback; default created_at sort.
//
// The status column is shown on every tab — redundant on the
// status-specific tabs but useful as a sanity-check (and zero cost),
// and load-bearing on the All tab.
// Per-tab status-date column config. Each tab picks the date that's
// most meaningful for its rows and gives the column header the right
// label:
//
//   - draft     : no extra date column (drafts have no status date).
//   - scheduled : "Publishes"   → publish_at + inline "in 30m" relative.
//   - published : "Published"   → published_at timestamp.
//   - all       : "Status date" → published_at as the column key, but
//                                  renderCell substitutes publish_at for
//                                  scheduled rows (and blank for drafts)
//                                  so each row shows the date that
//                                  matches its status. Column key has to
//                                  be a real DB column for the PostgREST
//                                  adapter to select it; the renderCell
//                                  override does the per-row swap.
const TABS = [
  {
    key: "draft",
    label: "Draft",
    staticFilters: { status: "eq.draft" },
    defaultSort: { updated_at: "desc" },
    dateColumn: null,
  },
  {
    key: "scheduled",
    label: "Scheduled",
    staticFilters: { status: "eq.scheduled" },
    defaultSort: { publish_at: "asc" },
    dateColumn: { key: "publish_at", display_as: "Publishes" },
  },
  {
    key: "published",
    label: "Published",
    staticFilters: { status: "eq.published" },
    defaultSort: { published_at: "desc" },
    dateColumn: { key: "published_at", display_as: "Published" },
  },
  {
    key: "all",
    label: "All",
    staticFilters: {},
    defaultSort: { created_at: "desc" },
    dateColumn: { key: "published_at", display_as: "Status date" },
  },
];
const VALID_TAB_KEYS = new Set(TABS.map((t) => t.key));
const DEFAULT_TAB = "draft";

function getTabFromUrl() {
  if (typeof window === "undefined") return DEFAULT_TAB;
  const tab = new URL(window.location.href).searchParams.get("tab");
  return tab && VALID_TAB_KEYS.has(tab) ? tab : DEFAULT_TAB;
}

function setTabInUrl(tab) {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", tab);
  window.history.replaceState({}, "", url.toString());
}

function StoryIdeasIndex() {
  // Memoize headers to prevent unnecessary re-renders of PostgRESTTable
  const memoizedHeaders = useMemo(() => authenticatedHeadersHash(), []);

  // ?highlight=<id> arrives when StoryIdeaFormPage redirects here after a
  // successful create. We flash the matching row for HIGHLIGHT_DURATION_MS
  // and strip the param from the URL so a refresh doesn't replay it.
  const [highlightId, setHighlightId] = useState(() => {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("highlight");
  });

  useEffect(() => {
    if (!highlightId) return undefined;
    const url = new URL(window.location.href);
    url.searchParams.delete("highlight");
    window.history.replaceState({}, "", url.toString());
    const t = window.setTimeout(() => setHighlightId(null), HIGHLIGHT_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [highlightId]);

  const rowClassName = useCallback(
    (row) => (highlightId && String(row.id) === String(highlightId) ? "story-idea-row-highlight" : undefined),
    [highlightId],
  );

  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setTabInUrl(tab);
  }, []);

  // Browser back/forward should sync the active tab.
  useEffect(() => {
    const onPopState = () => setActiveTab(getTabFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const tabConfig = useMemo(() => TABS.find((t) => t.key === activeTab) || TABS[0], [activeTab]);

  // refreshKey is a remount-token for PostgRESTTable: when an action
  // mutates a row (schedule, revert, delete) or the countdown detects a
  // status flip, we bump the key to force the table to re-fetch. This
  // matches the existing PostgRESTTable refresh pattern in the codebase.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshTable = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Inline error banner for failed actions. The PostgRESTTable doesn't
  // own this — StoryIdeaActions tells us, we show it.
  const [actionError, setActionError] = useState(null);
  const errorTimerRef = useRef(null);
  const reportActionError = useCallback((msg) => {
    setActionError(msg);
    if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    errorTimerRef.current = window.setTimeout(() => setActionError(null), 6000);
  }, []);
  useEffect(
    () => () => {
      if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    },
    [],
  );

  const renderCell = useCallback(
    (row, col) => {
      if (col === "from_name") {
        return (
          <>
            {row.from_name}
            {row.from_deleted && <span className="badge bg-danger ms-2">Deleted</span>}
          </>
        );
      }
      if (col === "status") {
        // Badge only — the status-specific date lives in its own
        // per-tab column (see TABS[…].dateColumn). Keeps this cell
        // visually quiet and consistent across tabs.
        return <StoryIdeaStatusBadge status={row.status} />;
      }
      // Scheduled tab — "Publishes" column. PublishCountdown owns the
      // relative-time rendering (with absolute on hover) AND the
      // polling that flips the row to Published in-place once the
      // publish job has run.
      if (activeTab === "scheduled" && col === "publish_at") {
        if (!row.publish_at) return null;
        return <PublishCountdown publishAt={row.publish_at} onStatusFlip={refreshTable} />;
      }
      // Published tab — "Published" column. Relative "2d ago" with
      // the absolute publish time on hover, for consistency with
      // the Scheduled tab's "in 30m" rendering.
      if (activeTab === "published" && col === "published_at") {
        return <RelativeTime date={row.published_at} />;
      }
      // All tab — "Status date" column. Substitute publish_at for
      // scheduled rows, blank for drafts, relative published_at for
      // published. Same relative+hover rendering used per-tab so the
      // column reads consistently across statuses.
      if (activeTab === "all" && col === "published_at") {
        if (row.status === "scheduled") {
          return <RelativeTime date={row.publish_at} />;
        }
        if (row.status === "draft") return null;
        // status === "published"
        return <RelativeTime date={row.published_at} />;
      }
      // Return undefined to use default rendering for other columns
      return undefined;
    },
    [activeTab, refreshTable],
  );

  const renderActions = useCallback(
    (row) => <StoryIdeaActions row={row} onMutate={refreshTable} onError={reportActionError} />,
    [refreshTable, reportActionError],
  );

  return (
    <div>
      <style>{`
        @keyframes story-idea-row-flash {
          0% { background-color: #fff3cd; }
          100% { background-color: transparent; }
        }
        tr.story-idea-row-highlight > td {
          animation: story-idea-row-flash ${HIGHLIGHT_DURATION_MS}ms ease-out;
        }
      `}</style>
      {actionError && (
        <Alert variant="danger" onClose={() => setActionError(null)} dismissible>
          {actionError}
        </Alert>
      )}

      <ul className="nav nav-tabs mb-0">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <PostgRESTTable
        // refreshKey bumps on mutation; activeTab bumps when admins
        // switch tabs so PostgRESTTable re-runs with the new staticFilters.
        key={`${activeTab}-${refreshKey}`}
        endpoint={`${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1`}
        resource="story_ideas"
        staticFilters={tabConfig.staticFilters}
        columns={(() => {
          // PostgREST adapter selects only the columns listed here, so
          // id must be present (hidden) — without it row.id is undefined
          // and the Edit/Schedule/Delete actions can't build their URLs.
          // publish_at / published_at default to hidden and are
          // selectively unhidden per tab via tabConfig.dateColumn.
          //
          // Column ORDER matters — Object.keys() drives the rendered
          // order in SearchableTable. Status + status-date sit adjacent
          // on the right side of the row (just left of Actions) so the
          // two related pieces of info read together AND sit near the
          // actions an admin is about to take based on them. The
          // dateColumn key (publish_at or published_at) lands AFTER
          // status in the key sequence so it also renders to the right
          // of the status badge.
          const base = {
            id: { type: "number", hidden: true },
            title: { type: "string" },
            news_hook: { type: "text" },
            topics_string: { type: "string", display_as: "topics" },
            from_name: { type: "string", display_as: "marketing persona" },
            from_deleted: { type: "boolean", hidden: true },
            updated_at: { type: "datetime", hidden: true },
            created_at: { type: "datetime" },
            status: { type: "string" },
            publish_at: { type: "datetime", hidden: true },
            published_at: { type: "datetime", hidden: true },
          };
          if (tabConfig.dateColumn) {
            base[tabConfig.dateColumn.key] = {
              type: "datetime",
              display_as: tabConfig.dateColumn.display_as,
            };
          }
          return base;
        })()}
        renderCell={renderCell}
        renderActions={renderActions}
        searchColumns={["title", "news_hook", "topics_string", "from_name"]}
        pageSize={PAGE_SIZE}
        headers={memoizedHeaders}
        defaultSort={tabConfig.defaultSort}
        rowClassName={rowClassName}
        toolbar={
          <>
            <Button
              variant="outline-secondary"
              href="https://qwoted.metabaseapp.com/dashboard/1288-newsjacking-automation-notifications"
              target="_blank"
              rel="noopener noreferrer"
              title="View Metabase Dashboard for Newsjacking Automation"
            >
              <i className="fa-solid fa-external-link me-1" aria-hidden="true" />
              &nbsp;Metabase
            </Button>
            <Button variant="primary" href="/admin/story_ideas/new">
              <i className="fa-solid fa-plus me-1" />
              &nbsp;Create
            </Button>
          </>
        }
      />
    </div>
  );
}

export default StoryIdeasIndex;
